/**
 * ReportPdfViewer — Phase-2 Slice 3: read-only PDF render of a delivered report.
 *
 * Salvaged from `src/_legacy/routes/ArtefactViewer.tsx` (the PdfViewer half):
 * pdfjs-dist v4 → one <canvas> per page. The legacy viewer ALSO carried a
 * Fabric.js annotation overlay + markup CRUD; that is deliberately DROPPED here
 * — Slice 3 is READ-ONLY (no annotation). The client opens their delivered
 * report; markup is a later slice.
 *
 * The URL is a SHORT-LIVED signed READ URL minted by the getReportDownloadUrl
 * CF (the parent fetches it; this component only renders). On any render error
 * the component falls back to a plain download link to the same URL so the
 * client is never dead-ended.
 *
 * 2026-06-04 — Anil (Phase-2 Slice 3).
 */
import { useEffect, useRef, useState } from 'react';

interface ReportPdfViewerProps {
  /** A signed READ URL for the report PDF (from getReportDownloadUrl). */
  url: string;
  /** Suggested download file name. */
  fileName?: string;
}

export default function ReportPdfViewer({ url, fileName }: ReportPdfViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [error, setError] = useState('');
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let pdfDoc: { destroy: () => void; numPages: number; getPage: (n: number) => Promise<unknown> } | null = null;

    (async () => {
      setError('');
      setRendering(true);
      try {
        const pdfjs = await import('pdfjs-dist');
        const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        (pdfjs as unknown as { GlobalWorkerOptions: { workerSrc: string } }).GlobalWorkerOptions.workerSrc =
          (worker as { default: string }).default;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loadingTask = (pdfjs as any).getDocument({ url });
        pdfDoc = await loadingTask.promise;
        if (cancelled || !pdfDoc) return;
        setPageCount(pdfDoc.numPages);

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = '';

        for (let p = 1; p <= pdfDoc.numPages; p++) {
          if (cancelled) break;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const page: any = await pdfDoc.getPage(p);
          const viewport = page.getViewport({ scale: 1.4 });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.style.maxWidth = `${viewport.width}px`;
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto 16px';
          canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.10)';
          canvas.style.borderRadius = '4px';

          const ctx = canvas.getContext('2d');
          if (ctx) await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) break;
          container.appendChild(canvas);
        }
        if (!cancelled) setRendering(false);
      } catch (err) {
        if (cancelled) return;
        console.error('[ReportPdfViewer] render failed:', err);
        setError((err as { message?: string })?.message || 'Could not render the report.');
        setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      try { pdfDoc?.destroy(); } catch { /* ignore */ }
    };
  }, [url]);

  return (
    <div data-testid="report-pdf-viewer">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {rendering
            ? 'Loading your report…'
            : pageCount > 0
              ? `${pageCount} page${pageCount === 1 ? '' : 's'}`
              : ''}
        </span>
        <a
          href={url}
          download={fileName || 'report.pdf'}
          target="_blank"
          rel="noreferrer"
          className="btn3 gh"
          style={{ marginLeft: 'auto', textDecoration: 'none' }}
        >
          ↓ Download PDF
        </a>
      </div>
      {error ? (
        <div
          style={{
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            padding: 16,
            fontSize: 13.5,
            color: 'var(--ink-soft)',
          }}
        >
          We couldn't render the report in your browser ({error}). You can still
          {' '}
          <a href={url} download={fileName || 'report.pdf'} target="_blank" rel="noreferrer">
            download the PDF here
          </a>.
        </div>
      ) : (
        <div ref={containerRef} />
      )}
    </div>
  );
}
